import { useCallback } from "react";
import { useNavigate, useParams } from "@tanstack/react-router";
import { Button } from "@devhop/ui";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { useSopDetailQuery } from "../api/queries";
import { trainingRoutes } from "../trainingRoutes";
import {
  TrainingSopDetailContent,
  type TrainingSopDetailViewModel,
} from "../ui/TrainingSopDetailContent";
import { ArrowLeftIcon, DownloadIcon, HelpCircle } from "lucide-react";
import { TrainingTour } from "../tour/TrainingTour";
import { useTrainingTour } from "../tour/useTrainingTour";
import { sopDetailTourSteps } from "../tour/trainingTourSteps";

/** Matches `trainingListSearchSchema` on `/app/training/sop` (list applies defaults). */
const defaultSopListSearch = {
  sortBy: undefined as string | undefined,
  sortOrder: undefined as "asc" | "desc" | undefined,
};

type SopDetailApiRow = {
  id: string;
  title: string;
  purpose: string | null;
  scope: string | null;
  updatedAt: Date | string;
  departmentName: string | null;
  steps: Array<{
    id: string;
    stepNumber: number;
    title: string;
    description: string | null;
    notes: string | null;
  }>;
};

function toViewModel(row: SopDetailApiRow): TrainingSopDetailViewModel {
  return {
    id: row.id,
    title: row.title,
    status: "active",
    purpose: row.purpose,
    scope: row.scope,
    departmentName: row.departmentName,
    responsiblePersonName: null,
    updatedAt: row.updatedAt,
    positionTags: [],
    steps: row.steps,
    documents: [],
    warnings: [],
  };
}

export function SopDetailPage() {
  const tour = useTrainingTour("training-sop-detail-tour-completed");
  const navigate = useNavigate({ from: "/app/training/sop/$id" });
  const { id } = useParams({ from: "/app/training/sop/$id" });
  const { data, isLoading } = useSopDetailQuery(id);
  const row = data as SopDetailApiRow | undefined;

  const goToList = useCallback(() => {
    navigate({
      to: trainingRoutes.sopList,
      search: defaultSopListSearch,
    });
  }, [navigate]);

  if (isLoading) {
    return (
      <>
        <Header />
        <Main>
          <div className="flex min-h-[400px] items-center justify-center">
            <div className="text-center">
              <div className="mx-auto h-8 w-8 animate-spin rounded-full border-primary border-b-2" />
              <p className="mt-2 text-muted-foreground">ກຳລັງໂຫຼດ...</p>
            </div>
          </div>
        </Main>
      </>
    );
  }

  if (!row?.id) {
    return (
      <>
        <Header />
        <Main>
          <div className="flex min-h-[400px] items-center justify-center">
            <div className="text-center">
              <h2 className="mb-2 font-bold text-2xl text-destructive">ບໍ່ພົບ SOP</h2>
              <p className="mb-4 text-muted-foreground">
                SOP ທີ່ທ່ານຊອກຫາບໍ່ມີຢູ່ ຫຼື ຖືກລຶບໄປແລ້ວ
              </p>
              <Button type="button" onClick={goToList} className="gap-2">
                <ArrowLeftIcon className="h-4 w-4" />
                ກັບໄປທີ່ລາຍການ SOP
              </Button>
            </div>
          </div>
        </Main>
      </>
    );
  }

  const sop = toViewModel(row);

  return (
    <>
      <Header />
      <Main>
        <div className="flex justify-between">
          <div className="mb-4 flex items-center gap-2" data-tourid="training-sop-detail-page-title">
            <h1 className="font-bold text-2xl tracking-tight">{sop.title}</h1>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="size-8"
              onClick={tour.startTour}
              aria-label="ເລີ່ມທົດສອບການນຳທາງ"
              title="ເລີ່ມທົດສອບການນຳທາງ"
            >
              <HelpCircle className="size-4" />
            </Button>
          </div>
          <div
            className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between"
            data-tourid="training-sop-detail-toolbar"
          >
            <Button type="button" variant="ghost" onClick={goToList} className="w-full gap-2 sm:w-auto">
              <ArrowLeftIcon className="h-4 w-4" />
              ກັບໄປທີ່ລາຍການ SOP
            </Button>
            <Button
              type="button"
              variant="outline"
              className="w-full gap-2 sm:w-auto"
              onClick={() =>
                navigate({
                  from: "/app/training/sop/$id",
                  to: trainingRoutes.sopPdfPreview,
                  params: { id },
                })
              }
            >
              <DownloadIcon className="h-4 w-4" />
              ເບິ່ງ PDF Preview
            </Button>

          </div>
        </div>



        <TrainingSopDetailContent sop={sop} />

        <TrainingTour steps={sopDetailTourSteps} run={tour.run} onCallback={tour.handleJoyrideCallback} />
      </Main>
    </>
  );
}
